import React from "react";
import { format } from "date-fns";
import { X, Users, Clock, Calendar as CalendarIcon, Loader2 } from "lucide-react";
import AsyncSelect from "react-select/async";
import { DateTimeUtil } from "../../../../utils";
import { SchedulingToolUtils } from "../../../../utils/schedulingToolConfig";
import type { SchedulingConfig } from "../../../../utils/schedulingToolConfig";
import type { PublishedShiftForAssign } from "../../../../types";

/** Shape of the actual open shift object (display/assign data) */
export interface OpenShiftData {
    shift_date?: string;
    shift_details?: {
        shift_name?: string;
        shift_code?: string;
        shift_start_time?: string;
        shift_end_time?: string;
        shift_id?: number;
    };
    manager_details?: { name?: string };
    open_shift_id?: number;
}

/** Payload: either wrapper { openShift, day } or the raw open shift object */
export type SelectedOpenShiftPayload =
    | { openShift?: OpenShiftData; day?: Date }
    | OpenShiftData;

export interface AssignOpenShiftModalProps {
    open: boolean;
    selectedOpenShift: SelectedOpenShiftPayload | null;
    schedulingConfig: Required<SchedulingConfig>;
    isLoadingPublishedShift: boolean;
    publishedShiftForAssign: PublishedShiftForAssign | null;
    selectedEmployeeForOpenShift: { value: string; label: string } | null;
    onClose: () => void;
    onSelectEmployee: (selected: { value: string; label: string } | null) => void;
    loadEmployeeOptions: (inputValue: string) => Promise<Array<{ value: string; label: string }>>;
    onAssign: () => void;
    isAssigning: boolean;
}

function formatTimeForParser(timeStr: string): string {
    if (!timeStr) return timeStr;
    if (timeStr.includes("T") || timeStr.includes(" ")) return timeStr;
    const timeWithSeconds =
        timeStr.length === 5 && timeStr.includes(":") ? `${timeStr}:00` : timeStr;
    return `2000-01-01 ${timeWithSeconds}`;
}

const AssignOpenShiftModal: React.FC<AssignOpenShiftModalProps> = ({
    open,
    selectedOpenShift,
    schedulingConfig,
    isLoadingPublishedShift,
    publishedShiftForAssign,
    selectedEmployeeForOpenShift,
    onClose,
    onSelectEmployee,
    loadEmployeeOptions,
    onAssign,
    isAssigning,
}) => {
    if (!open || !selectedOpenShift) return null;

    const openShift: OpenShiftData =
        typeof selectedOpenShift === "object" &&
        "openShift" in selectedOpenShift &&
        selectedOpenShift.openShift
            ? selectedOpenShift.openShift
            : (selectedOpenShift as OpenShiftData);
    const dayFromPayload =
        typeof selectedOpenShift === "object" && "day" in selectedOpenShift
            ? selectedOpenShift.day
            : undefined;

    const shiftDate =
        openShift.shift_date ||
        (dayFromPayload ? format(dayFromPayload, "yyyy-MM-dd") : "");

    let dateForFormatting: Date | null = null;
    if (dayFromPayload) {
        dateForFormatting = dayFromPayload;
    } else if (shiftDate) {
        dateForFormatting = new Date(shiftDate);
    }
    const formattedDate = dateForFormatting
        ? format(dateForFormatting, "EEEE, MMMM d, yyyy")
        : shiftDate;

    const isReadOnly = dateForFormatting
        ? !SchedulingToolUtils.canEditDate(dateForFormatting, schedulingConfig)
        : false;

    const startTimeRaw = openShift.shift_details?.shift_start_time || "";
    const endTimeRaw = openShift.shift_details?.shift_end_time || "";
    const startTimeFormatted = startTimeRaw.includes(":")
        ? startTimeRaw.length > 5
            ? startTimeRaw.substring(0, 5)
            : startTimeRaw
        : startTimeRaw;
    const endTimeFormatted = endTimeRaw.includes(":")
        ? endTimeRaw.length > 5
            ? endTimeRaw.substring(0, 5)
            : endTimeRaw
        : endTimeRaw;
    const startTime = startTimeFormatted ? formatTimeForParser(startTimeFormatted) : null;
    const endTime = endTimeFormatted ? formatTimeForParser(endTimeFormatted) : null;
    const timeRange =
        startTime && endTime
            ? DateTimeUtil.formatElegantTimeRange(startTime, endTime)
            : "";
    const displayTimeRange = timeRange && timeRange !== "N/A" ? timeRange : "";

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1500]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] m-4 flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Users className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                {isReadOnly ? "View Open Shift" : "Assign Open Shift"}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {formattedDate}
                                {isReadOnly && (
                                    <span className="ml-2 text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                                        Read-only (Past Date)
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    <div className="border-2 border-dashed border-purple-300 bg-purple-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <h4 className="font-semibold text-purple-900">
                                        {openShift.shift_details?.shift_name || "Open Shift"}
                                    </h4>
                                    {openShift.shift_details?.shift_code && (
                                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                                            {openShift.shift_details.shift_code}
                                        </span>
                                    )}
                                </div>
                                <div className="space-y-1 text-sm text-purple-700">
                                    {displayTimeRange && (
                                        <div className="flex items-center gap-2">
                                            <Clock size={14} />
                                            <span>{displayTimeRange}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <CalendarIcon size={14} />
                                        <span>{formattedDate}</span>
                                    </div>
                                    {openShift.manager_details?.name && (
                                        <div className="text-xs text-purple-600 mt-2">
                                            Created by: {openShift.manager_details.name}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assign to Employee *
                        </label>
                        {isLoadingPublishedShift ? (
                            <div className="flex items-center justify-center py-4 border border-gray-200 rounded-lg bg-gray-50">
                                <Loader2
                                    className="animate-spin text-purple-600 mr-2"
                                    size={20}
                                />
                                <span className="text-sm text-gray-600">
                                    Loading eligible employees...
                                </span>
                            </div>
                        ) : publishedShiftForAssign?.eligible_employees.length === 0 ? (
                            <div className="text-sm text-gray-500 italic py-4 border border-gray-200 rounded-lg bg-gray-50 px-4">
                                No eligible employees available for this shift
                            </div>
                        ) : (
                            <AsyncSelect
                                value={selectedEmployeeForOpenShift}
                                onChange={(selected: any) => onSelectEmployee(selected)}
                                loadOptions={loadEmployeeOptions}
                                placeholder={
                                    publishedShiftForAssign?.eligible_employees
                                        ? "Select or search employee..."
                                        : "Search employee (min 3 characters)..."
                                }
                                noOptionsMessage={({ inputValue }) =>
                                    inputValue.length < 3
                                        ? "Type at least 3 characters to search"
                                        : "No employees found"
                                }
                                className="text-sm"
                                classNamePrefix="react-select"
                                isClearable
                                isSearchable
                                isDisabled={isReadOnly}
                                defaultOptions={
                                    publishedShiftForAssign?.eligible_employees
                                        ? publishedShiftForAssign.eligible_employees.map(
                                              (emp) => ({
                                                  value: emp.emp_id,
                                                  label: emp.emp_name,
                                              })
                                          )
                                        : undefined
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: "42px",
                                        borderRadius: "0.5rem",
                                        borderColor: "#d1d5db",
                                        "&:hover": {
                                            borderColor: "#9ca3af",
                                        },
                                    }),
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 1600,
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 1600,
                                    }),
                                }}
                            />
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                            {publishedShiftForAssign?.eligible_employees
                                ? `${publishedShiftForAssign.eligible_employees.length} eligible employee(s) available. Search to filter.`
                                : "Search for an employee to assign this open shift to"}
                        </p>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onAssign}
                        disabled={
                            isAssigning ||
                            !selectedEmployeeForOpenShift ||
                            isReadOnly
                        }
                        className="px-4 py-2 bg-transparent border border-purple-600 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        title={
                            isReadOnly ? "Cannot assign shifts for past dates" : undefined
                        }
                    >
                        {isAssigning ? (
                            <>
                                <Loader2 className="animate-spin" size={16} />
                                Assigning...
                            </>
                        ) : (
                            "Assign Shift"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignOpenShiftModal;
